import { Component, WritableSignal, computed, inject, input, output, viewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { of, pipe, switchMap, map } from 'rxjs';
import { NgbDatepickerModule, NgbModal, NgbModalOptions, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { Action, SimpleGridComponent } from '@app/shared/components/simple-grid/simple-grid.component';
import {
  ReservationDriversUpsertDialogComponent,
  ReservationDriversUpsertDialogComponentConfig
} from './reservation-drivers-upsert-dialog/reservation-drivers-upsert-dialog.component';
import { ColDef } from 'ag-grid-community';
import { DeleteEventDataModel } from '@app/shared/components/simple-grid/simple-grid.model';
import { ReservationVersion } from '../reservations.model';
import { ReservationDriverDetailsModel } from './reservation-drivers.model';

type ReservationDriversConfig =
  | {
      reservationMode: 'create';
      reservationUid: string;
      drivers: WritableSignal<ReservationDriverDetailsModel[]>;
    }
  | {
      reservationMode: 'update';
      reservationUid: string;
      reservationVersion: ReservationVersion;
      drivers: WritableSignal<ReservationDriverDetailsModel[]>;
    }
  | {
      reservationMode: 'readonly';
      reservationUid: string;
      reservationVersion: ReservationVersion;
      drivers: WritableSignal<ReservationDriverDetailsModel[]>;
    };

@Component({
  standalone: true,
  selector: 'app-reservation-drivers',
  templateUrl: './reservation-drivers.component.html',
  imports: [ReactiveFormsModule, NgbNavModule, NgbDatepickerModule, SimpleGridComponent]
})
export class ReservationDriversComponent {
  private _modalService = inject(NgbModal);

  mainDriverExists = false;

  config = input.required<ReservationDriversConfig>();

  // Emitted after a driver is added, edited or removed, so the parent dialog can mark the
  // form dirty and include the drivers in the update payload.
  driversChanged = output<void>();

  private _grid = viewChild(SimpleGridComponent);
  private _items = computed(() => this.config().drivers());

  refresh() {
    this._grid()?.refresh();
  }

  private isReadonly = computed(() => this.config().reservationMode === 'readonly');

  baseRowActions = computed<Action[]>(() => (this.isReadonly() ? ['details'] : ['create', 'update', 'delete']));

  listReservationsOperator = pipe(
    map(() => {
      const items = this._items();
      this.mainDriverExists = items.some((x) => !x.isAdditionalDriver);
      return items;
    })
  );

  createReservationOperator = pipe(
    switchMap(() => {
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'create',
        reservationUid: this.config().reservationUid,
        onlyAdditionalDriver: this.mainDriverExists
      } as ReservationDriversUpsertDialogComponentConfig);
      return modalRef.closed.pipe(
        map((value) => {
          this.config().drivers.update((list) => [...list, value]);
        })
      );
    })
  );

  updateReservationOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'update',
        onlyAdditionalDriver: this.mainDriverExists,
        driver: existing
      } as ReservationDriversUpsertDialogComponentConfig);
      return modalRef.closed.pipe(
        map((value) => {
          this.config().drivers.update((list) => list.map((x) => (x.uid === uid ? value : x)));
        })
      );
    })
  );

  detailsReservationDriverOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'readonly',
        driver: existing
      } as ReservationDriversUpsertDialogComponentConfig);
      return modalRef.closed;
    })
  );

  deleteReservationOperator = pipe(
    switchMap((event: DeleteEventDataModel) => {
      this.config().drivers.update((list) => list.filter((x) => x.uid !== event.uid));
      return of(undefined);
    })
  );

  columnDefs: ColDef[] = [
    {
      field: 'firstName',
      filter: false,
      floatingFilter: false
    },
    {
      field: 'lastName',
      filter: false,
      floatingFilter: false
    },
    {
      field: 'mobile',
      filter: false,
      floatingFilter: false
    },
    {
      field: 'licenseNumber',
      filter: false,
      floatingFilter: false
    },
    {
      field: 'email',
      filter: false,
      floatingFilter: false
    },
    {
      field: 'isAdditionalDriver',
      filter: false,
      floatingFilter: false
    }
  ];

  rowActionSelector = () => [];
}
